'use es6';

import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { Big } from 'foundations-components/transitional/components/Big';
import { H1 } from 'foundations-components/transitional/components/H1';
import { HubspotCertificationIcon } from 'foundations-assets';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Breadcrumbs } from 'foundations-components/transitional/components/Breadcrumbs';
import { Button } from 'foundations-components/transitional/components/Button';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { Image } from 'foundations-components/transitional/components/Image';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { MediaObject } from 'foundations-components/transitional/components/MediaObject';
import { Tab, Tabs } from 'foundations-components/transitional/components/TabsTransitionalOld';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { getHasAppCertificationCredential } from 'marketplace-ui-apps-core/common/utils/Certification';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import { MediaQueryView } from 'marketplace-ui-common/layout/components/MediaQueryView';
import * as ScreenSizes from 'marketplace-ui-common/layout/constants/ScreenSizes';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo } from 'react';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import * as CustomPropTypes from '../../constants/PropTypes';
import { useLeadGenContext } from '../../context/LeadGenContext';
import { getEventSelectorId } from '../../utils/analytics';
import { getIsStandardConnection } from '../../utils/offerings';
import { getFormattedUrl } from '../../utils/urls';
import { ConnectButton } from './connect/ConnectButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const TAB_IDS = {
  OVERVIEW: 'overview',
  FEATURES: 'features',
  SHARED_DATA: 'sharedData',
  PRICING: 'pricing',
  REVIEWS: 'reviews'
};
const Header = ({
  activeTab,
  appId,
  appName,
  appUrl,
  breadcrumbs,
  credentials,
  connectionType,
  features,
  headingLevel = 'h1',
  iconUrl,
  interactionDetails,
  isPreview,
  isDataSyncApp,
  listingId,
  offeringId,
  onConnectButtonClick,
  onTrackEvent,
  onTrackInteraction,
  onTrackPageview,
  scrollToTabSection,
  showTabs = true,
  showConnectButton = true,
  showSetUpUrl = true,
  setupUrl,
  tagline,
  isCustomer
}) => {
  const {
    runLeadGen
  } = useLeadGenContext();
  const {
    isMobileView
  } = useResponsiveProps({
    isMobileView: {
      [ScreenSizes.XS]: true,
      [ScreenSizes.MD]: false
    }
  });
  const renderedConnectButton = useMemo(() => {
    const standardConnection = getIsStandardConnection(connectionType);
    const oauthUrlConfigured = standardConnection && !appUrl;
    return isPreview && !oauthUrlConfigured ? /*#__PURE__*/_jsx(Button, {
      use: "primary",
      disabled: true,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-ui-apps-core.installButton.install"
      })
    }) : /*#__PURE__*/_jsx(ConnectButton, {
      appId: appId,
      appName: appName,
      appUrl: appUrl,
      connectionType: connectionType,
      features: features,
      isCustomer: isCustomer,
      isDataSyncApp: isDataSyncApp,
      isPreview: isPreview,
      listingId: listingId,
      offeringId: offeringId,
      onConnectButtonClick: onConnectButtonClick,
      onTrackEvent: onTrackEvent,
      onTrackInteraction: onTrackInteraction,
      onTrackPageview: onTrackPageview
    });
  }, [appId, appName, appUrl, connectionType, features, isCustomer, isPreview, isDataSyncApp, listingId, offeringId, onConnectButtonClick, onTrackEvent, onTrackInteraction, onTrackPageview]);
  const renderedSetUpUrl = useMemo(() => /*#__PURE__*/_jsx(Button, {
    external: true,
    href: getFormattedUrl(setupUrl),
    onClick: () => {
      onTrackInteraction('click view setup guide', {
        section: 'header'
      });
      runLeadGen({
        onTrackInteraction,
        section: 'header'
      });
    },
    use: "transparent",
    "data-hs-analytics-id": getEventSelectorId('HEADER-SETUP-GUIDE'),
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "appMarketplace.detail.header.viewSetupGuideLinkText"
    })
  }), [onTrackInteraction, runLeadGen, setupUrl]);
  const meetingUrl = useMemo(() => {
    var _interactionDetails$o;
    return interactionDetails === null || interactionDetails === void 0 || (_interactionDetails$o = interactionDetails.options) === null || _interactionDetails$o === void 0 || (_interactionDetails$o = _interactionDetails$o.find(option => option.type === 'MEETING')) === null || _interactionDetails$o === void 0 ? void 0 : _interactionDetails$o.value;
  }, [interactionDetails]);
  const formUrl = useMemo(() => {
    var _interactionDetails$o2;
    return interactionDetails === null || interactionDetails === void 0 || (_interactionDetails$o2 = interactionDetails.options) === null || _interactionDetails$o2 === void 0 || (_interactionDetails$o2 = _interactionDetails$o2.find(option => option.type === 'FORM')) === null || _interactionDetails$o2 === void 0 ? void 0 : _interactionDetails$o2.value;
  }, [interactionDetails]);
  const renderedScheduleDemoButton = useMemo(() => {
    return meetingUrl && formUrl ? /*#__PURE__*/_jsx(Dropdown, {
      buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "appMarketplace.detail.header.scheduleDemoButton"
      }),
      dropdownStyles: {
        marginInline: '100'
      },
      buttonUse: "secondary",
      marginInline: "600",
      children: /*#__PURE__*/_jsxs(List, {
        children: [/*#__PURE__*/_jsx(ListItem, {
          children: /*#__PURE__*/_jsx(Button, {
            href: meetingUrl,
            external: true,
            onClick: () => onTrackInteraction('click schedule meeting', {
              section: 'header'
            }),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "appMarketplace.detail.header.scheduleMeetingButton"
            })
          })
        }), /*#__PURE__*/_jsx(ListItem, {
          children: /*#__PURE__*/_jsx(Button, {
            href: formUrl,
            external: true,
            onClick: () => onTrackInteraction('click request to be contacted', {
              section: 'header'
            }),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "appMarketplace.detail.header.requestToBeContactedButton"
            })
          })
        })]
      })
    }) : /*#__PURE__*/_jsx(Button, {
      href: meetingUrl || formUrl,
      external: true,
      onClick: () => onTrackInteraction('click schedule demo', {
        section: 'header'
      }),
      marginInlineEnd: "200",
      use: "secondary",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: meetingUrl ? 'appMarketplace.detail.header.scheduleDemoButton' : 'appMarketplace.detail.header.requestToBeContactedButton'
      })
    });
  }, [meetingUrl, formUrl, onTrackInteraction]);
  const renderedButtonRow = useMemo(() => /*#__PURE__*/_jsxs(Flex, {
    flexDirection: isMobileView ? 'column-reverse' : 'row',
    inlineSize: isMobileView ? '100%' : 'auto',
    children: [showSetUpUrl && renderedSetUpUrl, (meetingUrl || formUrl) && renderedScheduleDemoButton, showConnectButton && renderedConnectButton]
  }), [isMobileView, renderedConnectButton, renderedScheduleDemoButton, renderedSetUpUrl, showConnectButton, showSetUpUrl, meetingUrl, formUrl]);
  const trackBreadcrumbLinkClick = useCallback(value => {
    onTrackInteraction('click breadcrumb link', {
      section: 'header',
      value
    });
  }, [onTrackInteraction]);
  const trackTabClick = useCallback(tabId => {
    onTrackInteraction('click tab navigation', {
      section: 'header',
      value: tabId
    });
  }, [onTrackInteraction]);
  const defaultBreadcrumbs = useMemo(() => !isPreview ? /*#__PURE__*/_jsxs(Breadcrumbs, {
    flush: true,
    marginBlockStart: "0",
    children: [/*#__PURE__*/_jsx(UIRouterLink, {
      onClick: () => trackBreadcrumbLinkClick('all apps'),
      to: "/",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "appMarketplace.detail.header.rootBreadcrumbLinkText"
      })
    }), /*#__PURE__*/_jsx("span", {
      children: appName
    })]
  }) : null, [appName, isPreview, trackBreadcrumbLinkClick]);
  const renderedCertifiedLabel = useMemo(() => {
    if (getHasAppCertificationCredential(credentials)) {
      return /*#__PURE__*/_jsx(Tooltip, {
        placement: "bottom",
        title: unescapedText('appMarketplace.detail.header.certifiedLabel.tooltip'),
        children: /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          flexDirection: "row",
          marginBlockStart: "50",
          gap: "100",
          children: [/*#__PURE__*/_jsx(HubspotCertificationIcon, {
            color: "do-not-use-lorax",
            size: "600"
          }), /*#__PURE__*/_jsx(Text, {
            textTransform: "uppercase",
            typography: "heading-25",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "appMarketplace.detail.header.certifiedLabel.text"
            })
          })]
        })
      });
    } else {
      return null;
    }
  }, [credentials]);
  const renderedHeading = useMemo(() => {
    if (headingLevel === 'h2') {
      return /*#__PURE__*/_jsx(Text, {
        as: "span",
        "data-test-id": "details-heading",
        marginBlockEnd: "0",
        typography: "heading-600",
        children: appName
      });
    }
    return /*#__PURE__*/_jsx(H1, {
      "data-test-id": "details-heading",
      "aria-level": headingLevel === 'h1' ? 1 : null,
      marginBlockEnd: "0",
      children: appName
    });
  }, [appName, headingLevel]);
  const onTabClick = useCallback((tabName, evt) => {
    evt.preventDefault();
    trackTabClick(tabName);
    return scrollToTabSection(tabName);
  }, [scrollToTabSection, trackTabClick]);
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash && Object.values(TAB_IDS).includes(hash)) {
      scrollToTabSection(hash);
    }
  }, [scrollToTabSection]);
  return /*#__PURE__*/_jsxs(View, {
    inlineSize: "100%",
    paddingBlockStart: "400",
    paddingBlockEnd: showTabs ? '0' : '600',
    children: [/*#__PURE__*/_jsx(View, {
      marginBlockEnd: "600",
      as: "div",
      children: breadcrumbs || defaultBreadcrumbs
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: isMobileView ? 'column' : 'row',
      alignItems: "center",
      justifyContent: isMobileView ? undefined : 'space-between',
      children: [/*#__PURE__*/_jsx(MediaObject, {
        itemLeft: !isMobileView && /*#__PURE__*/_jsx(Image, {
          src: iconUrl,
          maxInlineSize: "85px",
          maxBlockSize: "85px"
        }),
        children: /*#__PURE__*/_jsxs(View, {
          marginBlockEnd: isMobileView ? '300' : undefined,
          children: [/*#__PURE__*/_jsx(MediaObject, {
            align: "center",
            itemLeft: isMobileView && /*#__PURE__*/_jsx(Image, {
              src: iconUrl,
              maxInlineSize: "32px"
            }),
            children: renderedHeading
          }), /*#__PURE__*/_jsx(View, {
            marginBlockEnd: "100",
            as: "div",
            children: /*#__PURE__*/_jsx(Big, {
              children: tagline
            })
          }), renderedCertifiedLabel]
        })
      }), renderedButtonRow]
    }), showTabs && /*#__PURE__*/_jsx(Flex, {
      order: 4,
      paddingBlockStart: "300",
      children: /*#__PURE__*/_jsx(MediaQueryView, {
        min: "md",
        children: /*#__PURE__*/_jsx(Tabs, {
          bordered: false,
          defaultSelected: TAB_IDS.OVERVIEW,
          minInlineSize: "1280px",
          selected: activeTab,
          children: Object.values(TAB_IDS).map(tab => /*#__PURE__*/_jsx(Tab, {
            "data-test-id": `details-tab-${tab}`,
            onClick: evt => onTabClick(tab, evt),
            tabId: tab,
            title: unescapedText(`appMarketplace.detail.header.tabTitles.${tab}`)
          }, tab))
        })
      })
    })]
  });
};
Header.propTypes = {
  activeTab: PropTypes.string,
  appId: PropTypes.number,
  appName: PropTypes.string,
  appUrl: PropTypes.string,
  breadcrumbs: PropTypes.node,
  credentials: PropTypes.arrayOf(PropTypes.number).isRequired,
  connectionType: CustomPropTypes.connectionType,
  headingLevel: PropTypes.oneOf(['h1', 'h2']),
  iconUrl: PropTypes.string.isRequired,
  isCustomer: PropTypes.bool.isRequired,
  isPreview: PropTypes.bool,
  isDataSyncApp: PropTypes.bool,
  listingId: PropTypes.number,
  offeringId: PropTypes.number,
  onConnectButtonClick: PropTypes.func,
  onTrackEvent: PropTypes.func,
  onTrackInteraction: PropTypes.func,
  onTrackPageview: PropTypes.func,
  tagline: PropTypes.string.isRequired,
  scrollToTabSection: PropTypes.func,
  showTabs: PropTypes.bool.isRequired,
  showConnectButton: PropTypes.bool.isRequired,
  showSetUpUrl: PropTypes.bool.isRequired,
  setupUrl: PropTypes.string,
  wrapperStyle: PropTypes.object
};
export default Header;